import { createListCollection } from '@chakra-ui/react';
import type { UserSubscriptionRow } from '../../types';

/** Sentinel select values (not levels). */
export const ROLE_DEFAULT = 'default';
export const UNSUBSCRIBED = 'unsubscribed';

const LEVEL_LABELS = (t: any): Record<string, string> => ({
  '0': t('alert_start'),
  '1': t('first_escalation'),
  '2': t('second_escalation'),
});

/**
 * With ADR-117 Mechanism B off, levels above 0 do not exist as far
 * as the UI is concerned — an already-stored 1/2 (rubric resolution OR a user
 * override) displays as level 0, i.e. the "Alert Start" label. Presentation
 * only: nothing here writes, so the stored value survives untouched and
 * re-enabling the flag brings the real level straight back.
 */
function displayLevel(level: number, escalationEnabled: boolean): number {
  return escalationEnabled || level <= 0 ? level : 0;
}

export function levelLabel(
  level: number | null,
  escalationEnabled: boolean,
  t: any,
): string {
  if (level == null) return t('unsubscribed');
  const shown = displayLevel(level, escalationEnabled);
  return LEVEL_LABELS(t)[String(shown)] ?? t('level_format', { level: shown });
}

/** The currently-selected option value for one axis of a row. */
export function axisValue(
  row: UserSubscriptionRow,
  axis: 'alert' | 'drill',
  escalationEnabled: boolean,
): string {
  if (!row.is_override) return ROLE_DEFAULT;
  const level = axis === 'alert' ? row.alert_min_level : row.drill_min_level;
  if (level == null) return UNSUBSCRIBED;
  // Clamp so an existing level-1/2 override still selects an option the
  // collection actually contains (otherwise the closed dropdown renders
  // empty). Display-only — the override row is never rewritten.
  return String(displayLevel(level, escalationEnabled));
}

/**
 * The option list for one axis. "Role default" shows the resolved rubric
 * level; "Unsubscribed" appears only when allowed (always for drill; for
 * alert only when `allow_unsubscribe`, i.e. non-emergency) AND the caller can
 * manage subscriptions. The escalation levels (1/2) appear only when ADR-117
 * Mechanism B is enabled for the site — the same
 * caller-injected-boolean shape as `canUnsubscribe`.
 */
export function buildCollection(
  row: UserSubscriptionRow,
  axis: 'alert' | 'drill',
  canUnsubscribe: boolean,
  escalationEnabled: boolean,
  t: any,
) {
  const resolved =
    axis === 'alert'
      ? row.resolved_alert_min_level
      : row.resolved_drill_min_level;
  const allowUnsubscribe =
    canUnsubscribe && (axis === 'drill' || row.allow_unsubscribe);
  return createListCollection({
    items: [
      {
        label: t('role_default_with_level', {
          level: levelLabel(resolved, escalationEnabled, t),
        }),
        value: ROLE_DEFAULT,
      },
      ...(allowUnsubscribe
        ? [{ label: t('unsubscribed'), value: UNSUBSCRIBED }]
        : []),
      { label: t('alert_start'), value: '0' },
      ...(escalationEnabled
        ? [
            { label: t('first_escalation'), value: '1' },
            { label: t('second_escalation'), value: '2' },
          ]
        : []),
    ],
  });
}

export function lockCopy(lock: 'follow' | 'group', t: any): string {
  return lock === 'group'
    ? t('managed_by_group_membership')
    : t('always_subscribed');
}
